<template>
  <div class="credits-utilization" :class="{'-has-error': errors.cantFetchChartData}">
    <div class="details">
      <h2 class="title">
        Utilização de créditos
        <span class="errorMessage" v-if="errors.cantFetchChartData">Erro</span>
      </h2>

      <p class="subtitle">
        <skeleton-loader width="171px" height="36px" v-if="loading"/>
        <span v-else>R$ {{ total | currency }}</span>
      </p>
    </div>

    <div class="chart">
      <area-chart-skeleton-loader
        :quantity-series="3"
        v-if="loading"
      />
      <apex-chart
        type="area"
        height="350"
        :options="chartOptions"
        :series="chartSeries"
        v-else
      ></apex-chart>
    </div>
  </div>
</template>

<script>
import ApexChart from 'vue-apexcharts';
import SkeletonLoader from '@/support/components/loader/SkeletonLoader.vue';
import AreaChartSkeletonLoader from '@/support/components/loader/AreaChartSkeletonLoader.vue';
import currency from '@/support/filters/currency';

export default {
  name: 'CreditsUtilization',

  props: {
    period: {
      required: true,
      type: Number,
    },
  },

  data() {
    return {
      loading: false,
      total: 0,
      chartSeries: [],
      chartOptionsXaxisCategories: [],
      errors: {
        cantFetchChartData: false,
      },
    };
  },

  methods: {
    /**
     * Fetch the chart data.
     *
     * @returns {Promise<void>}
     */
    async fetchChartData() {
      try {
        this.resetErrors();
        this.resetChartData();
        this.loading = true;

        const { data } = await this.$http.get(`metrics/charts/credits-utilization?period=${this.period}`);

        this.chartSeries = [...data.series];
        this.chartOptionsXaxisCategories = [...data.categories];
        this.total = data.total;
      } catch {
        this.errors.cantFetchChartData = true;
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Ocorreu um erro',
          text: 'Não foi possível carregar sua utilização de créditos.',
        });
      } finally {
        this.loading = false;
      }
    },

    /**
     * Reset the errors to initial values.
     */
    resetErrors() {
      this.errors.cantFetchChartData = false;
    },

    /**
     * Reset chart data.
     */
    resetChartData() {
      this.total = 0;
      this.chartSeries = [];
      this.chartOptionsXaxisCategories = [];
    },
  },

  computed: {
    chartOptions() {
      return {
        chart: {
          height: window.screen.availHeight > 768 ? 350 : 300,
          type: 'area',
          toolbar: {
            show: false,
          },
          sparkline: {
            enabled: true,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
          width: 0,
        },
        xaxis: {
          type: 'datetime',
          categories: this.chartOptionsXaxisCategories,
          labels: {
            show: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        yaxis: {
          low: 0,
          offsetX: 0,
          offsetY: 0,
          show: false,
          labels: {
            low: 0,
            offsetX: 0,
            show: false,
          },
          axisBorder: {
            low: 0,
            offsetX: 0,
            show: false,
          },
        },
        tooltip: {
          x: {
            format: 'dd/MM/yy HH:mm',
          },
        },
        legend: {
          show: false,
        },
        fill: {
          type: 'gradient',
          opacity: [1, 0.4, 0.25],
          gradient: {
            shade: 'light',
            type: 'horizontal',
            shadeIntensity: 1,
            gradientToColors: ['#a26cf8', '#a927f9', '#7366ff'],
            opacityFrom: [1, 0.4, 0.25],
            opacityTo: [1, 0.4, 0.25],
            stops: [30, 100],
            colorStops: [],
          },
          colors: ['#a26cf8', '#a26cf8', '#a26cf8'],
        },
        colors: ['#a26cf8', '#f73164', '#a26cf8'],
      };
    },
  },

  watch: {
    period() {
      this.fetchChartData();
    },
  },

  mounted() {
    this.fetchChartData();
  },

  filters: {
    currency,
  },

  components: {
    SkeletonLoader,
    AreaChartSkeletonLoader,
    ApexChart,
  },
};
</script>

<style lang="scss" scoped>
.credits-utilization {
  overflow: hidden;
  @apply bg-white;
  @apply w-full;
  @apply flex;
  @apply flex-col;
  @apply justify-end;

  > .details {
    @apply p-8;
    @apply relative;
    @apply self-start;
    @apply flex-1;
    z-index: 2;
  }

  > .details .title {
    @apply text-secondary-700;
    @apply uppercase;
    @apply text-sm;
    @apply font-bold;
    letter-spacing: .05em;
  }

  > .details .subtitle {
    @apply font-bold;
    @apply text-secondary-900;
    @apply text-3xl;
    @apply absolute;
    @apply mt-4;
  }

  > .chart {
    @apply w-full;
  }

  &.-has-error {
    > .details .title {
      @apply text-red-500;
    }

    > .details .title .errorMessage {
      text-transform: none;
      font-weight: normal;

      &:before {
        content: '- ';
      }
    }
  }
}
</style>
